import { Component, DestroyRef, ElementRef, HostListener, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { MenuService } from '../../../../core/services/menu.service';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule, FormsModule, InputTextModule],
    template: `
        <div class="menu-search">
            <i class="pi pi-search menu-search-icon"></i>
            <input
                #searchInput
                type="text"
                pInputText
                class="menu-search-input"
                placeholder="Search menu..."
                autocomplete="off"
                [(ngModel)]="searchTerm"
                (ngModelChange)="onSearchChange()"
            />
            <i class="pi pi-times menu-search-clear" *ngIf="searchTerm" (click)="clearSearch()"></i>
        </div>
        <ul class="layout-menu">
            <ng-container *ngFor="let item of filteredModel; let i = index">
                <li class="items" app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true" [forceExpand]="isSearching"></li>
                <li *ngIf="item.separator" class="menu-separator fill-black"></li>
            </ng-container>
        </ul>
        <div class="menu-no-results" *ngIf="isSearching && filteredModel.length === 0">
            <i class="pi pi-search"></i>
            <span>No menu items match "{{ searchTerm }}"</span>
        </div>
    `,
    styles: `
        .items {
            color: #ffffff !important;
        }
    `
})
export class AppMenu {
    @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

    model: MenuItem[] = [];
    filteredModel: MenuItem[] = [];
    searchTerm = '';

    constructor(
        private readonly menuService: MenuService,
        private readonly destroyRef: DestroyRef
    ) {
        // AppMenu is rebuilt on most navigations (each feature route has its own AppLayout), so it
        // renders from the cached menu straight away and only triggers a fetch when that is stale.
        this.menuService.menu$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((menu) => {
            this.model = menu;
            this.applyFilter();
        });
        this.menuService.ensureLoaded();
    }

    get isSearching(): boolean {
        return this.searchTerm.trim().length > 0;
    }

    onSearchChange(): void {
        this.applyFilter();
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.applyFilter();
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            this.searchInputRef?.nativeElement?.focus();
        }
    }

    private applyFilter(): void {
        const term = this.searchTerm.trim().toLowerCase();
        this.filteredModel = term ? this.filterItems(this.model, term) : this.model;
    }

    private filterItems(items: MenuItem[], term: string): MenuItem[] {
        return items.reduce<MenuItem[]>((acc, item) => {
            if (item.separator) {
                return acc;
            }

            const labelMatch = !!item.label?.toLowerCase().includes(term);
            const filteredChildren = item.items ? this.filterItems(item.items, term) : undefined;

            if (labelMatch || (filteredChildren && filteredChildren.length > 0)) {
                acc.push({
                    ...item,
                    items: item.items ? (labelMatch ? item.items : filteredChildren) : undefined
                });
            }

            return acc;
        }, []);
    }
}
